import React, { useState, useEffect } from 'react';
import { LogOut, ChevronDown, ChevronUp, Trash2, MessageSquare, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { feedbackService, authService } from '../services/api';

function AdminComplaintsSuggestions() {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        const response = await feedbackService.getAll();
        if (response.data) {
          setFeedbacks(response.data);
        }
      } catch (err) {
        setError('خطا در دریافت شکایات و پیشنهادات');
      } finally {
        setLoading(false);
      }
    };
    fetchFeedbacks();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await feedbackService.delete(id);
      setFeedbacks(feedbacks.filter(f => f.id !== id));
    } catch (err) {
      setError('خطا در حذف مورد');
    }
  };

  const typeLabel = (type) => {
    switch (type) {
      case 'complaint': return 'شکایت';
      case 'suggestion': return 'پیشنهاد';
      case 'bug': return 'گزارش خرابی';
      default: return 'سایر';
    }
  };

  const typeClass = (type) => {
    switch (type) {
      case 'complaint': return 'bg-red-100 text-red-700';
      case 'suggestion': return 'bg-blue-100 text-blue-700';
      case 'bug': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت شکایات و پیشنهادات</h1>
              <p className="text-xl text-blue-100">مشاهده و مدیریت شکایات و پیشنهادات دریافتی</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {feedbacks.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>هیچ شکایت یا پیشنهادی برای نمایش وجود ندارد</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbacks.map(feedback => (
              <div key={feedback.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{feedback.subject}</h3>
                      <p className="text-sm text-gray-600">{feedback.name} • {feedback.email}</p>
                      <p className="text-xs text-gray-500 mt-1">{feedback.date || feedback.createdAt}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${typeClass(feedback.type)}`}>
                        {typeLabel(feedback.type)}
                      </span>
                      <button
                        onClick={() => setExpandedFeedback(expandedFeedback === feedback.id ? null : feedback.id)}
                        className="p-2 hover:bg-gray-200 rounded-lg transition"
                      >
                        {expandedFeedback === feedback.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>
                  </div>

                  {expandedFeedback === feedback.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-gray-700 mb-4 whitespace-pre-wrap">{feedback.message}</p>
                      <button
                        onClick={() => handleDelete(feedback.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold text-sm"
                      >
                        <Trash2 size={16} />
                        حذف
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminComplaintsSuggestions;
